import React, { useState, useEffect, useCallback } from 'react';
import attendanceAPI from '../api/attendanceAPI';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';
import { 
  MdCalendarToday, 
  MdCheckCircle,
  MdCancel,
  MdAccessTime,
  MdBook
} from 'react-icons/md';

/**
 * Tutor Dashboard Component
 * Shows quick stats and links to tutor features
 */
function TutorDashboard() {
  const { user, getToken } = useAttendanceAuth();
  const [todayStats, setTodayStats] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTodayStats = useCallback(async ({ showLoading = false } = {}) => {
    try {
      if (showLoading) setLoading(true);
      const token = getToken();
      const [historyResult, todayResult, assignmentsResult] = await Promise.all([
        attendanceAPI.getHistory({ limit: 500 }, token).catch(() => ({ success: false, sessions: [] })),
        attendanceAPI.getTodaySessions(token).catch(() => ({ success: false, sessions: [] })),
        attendanceAPI.getTutorAssignments(token).catch(() => ({ success: false, assignments: [] }))
      ]);
      setAssignments(
        assignmentsResult?.success && Array.isArray(assignmentsResult.assignments)
          ? assignmentsResult.assignments
          : []
      );

      const historySessions = historyResult?.success && Array.isArray(historyResult.sessions)
        ? historyResult.sessions
        : [];
      const todaySessions = todayResult?.success && Array.isArray(todayResult.sessions)
        ? todayResult.sessions
        : [];

      // Merge history + today and dedupe by session ID for accurate rollup.
      const sessionMap = new Map();
      [...historySessions, ...todaySessions].forEach((session) => {
        const sessionId = session?.id ?? session?.session_id;
        if (sessionId === undefined || sessionId === null) return;
        sessionMap.set(String(sessionId), session);
      });

      // Cancelled sessions belong to ended/invalid schedules and must not be
      // counted as Tutor attendance work.
      const allSessions = Array.from(sessionMap.values()).filter(
        (session) => (session.state || session.status || '').toLowerCase() !== 'cancelled'
      );
      const marked = allSessions.filter(
        (session) => (session.state || session.status || '').toLowerCase() === 'marked'
      ).length;
      const missed = allSessions.filter(
        (session) => (session.state || session.status || '').toLowerCase() === 'missed'
      ).length;
      const pending = allSessions.filter(
        (session) => (session.state || session.status || '').toLowerCase() === 'pending'
      ).length;

      const plannedSummary = assignmentsResult?.success
        ? assignmentsResult.attendance_summary
        : null;
      setTodayStats({
        total: Number(plannedSummary?.total ?? allSessions.length),
        pending: Number(plannedSummary?.pending ?? pending),
        marked: Number(plannedSummary?.marked ?? marked),
        missed: Number(plannedSummary?.missed ?? missed)
      });
    } catch (err) {
      console.error('Error fetching today stats:', err);
      setTodayStats({ total: 0, pending: 0, marked: 0, missed: 0 });
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchTodayStats({ showLoading: true });

    // Keep dashboard counters fresh as time and Attendance status change.
    const refreshInterval = setInterval(fetchTodayStats, 60000);
    const handleWindowFocus = () => fetchTodayStats();
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      clearInterval(refreshInterval);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [fetchTodayStats]);

  const activeSemesterLabels = [...new Set(
    assignments.map(assignment => assignment.semester_label).filter(Boolean)
  )];
  const activeSemesterTitle = activeSemesterLabels.join(', ');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Welcome, {user?.name || 'Tutor'}
        </h1>
        <p className="text-gray-600">Tutor Dashboard - Manage your class attendance</p>
      </div>

      {/* Active subject assignments */}
      <section className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm mb-8">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Active Assigned Subjects{activeSemesterTitle ? ` — ${activeSemesterTitle}` : ''}
            </h2>
            <p className="text-sm text-gray-500">Subjects assigned to you in the current active timetable</p>
          </div>
          <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700">
            <MdBook /> {assignments.length}
          </span>
        </div>
        {assignments.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {assignments.map((assignment) => (
              <article key={assignment.id} className="rounded-lg border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900">
                  {assignment.subject_name || 'Unnamed subject'}
                </h3>

                <p className="mt-2 text-sm text-gray-600">
                  {[assignment.faculty_name, assignment.department_name]
                    .filter(Boolean)
                    .join(' · ') || 'Faculty information not assigned'}
                </p>
                {assignment.semester ? (
                  <p className="mt-1 text-xs text-gray-500">{assignment.semester_label || `Semester ${assignment.semester}`}</p>
                ) : null}
                {assignment.batch_names?.length ? (
                  <p className="mt-1 text-xs text-gray-500">Batch: {assignment.batch_names.join(', ')}</p>
                ) : null}
              </article>
            ))}
          </div>
        ) : (
          <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
            No active subject assignments were found for this employee.
          </p>
        )}
      </section>

      {/* Today's Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Attendance</p>
              <p className="text-2xl font-bold text-gray-800">{todayStats?.total || 0}</p>
            </div>
            <MdCalendarToday className="text-3xl text-blue-500" />
          </div>
        </div>

        <div className="bg-white border border-yellow-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{todayStats?.pending || 0}</p>
            </div>
            <MdAccessTime className="text-3xl text-yellow-500" />
          </div>
        </div>

        <div className="bg-white border border-green-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Marked</p>
              <p className="text-2xl font-bold text-green-600">{todayStats?.marked || 0}</p>
            </div>
            <MdCheckCircle className="text-3xl text-green-500" />
          </div>
        </div>

        <div className="bg-white border border-red-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Missed</p>
              <p className="text-2xl font-bold text-red-600">{todayStats?.missed || 0}</p>
            </div>
            <MdCancel className="text-3xl text-red-500" />
          </div>
        </div>
      </div>

    </div>
  );
}

export default TutorDashboard;

